/**
 * Whether a return from Logalty may open the signature ceremony by itself.
 *
 * Cancelling or rejecting the attempt is not terminal (the client can retry),
 * but the retry has to be an explicit click. Re-requesting a SAML for the same
 * ref_id on page load is what makes Logalty fail after a cancel.
 *
 * `cancelled` / `canceled` / `rejected by client` are the closed signature
 * attempt outcomes this resume map already uses. They are not in
 * ApplicationStatus, and they are not a later underwriting cancellation:
 * the request stays open and the client may sign again. A retry link must
 * not reopen the ceremony once the application has moved on
 * (`signed`, `pending evaluation`, `quality control`, `paid`). `in summary`
 * is not a retry either: the normal resume route is the confirmation page.
 */

const CLOSED_SIGNATURE_STATUSES = new Set([
  'rejected by client',
  'cancelled',
  'canceled',
]);

/**
 * Statuses where a new ceremony is legitimate: the attempt is still open
 * (`in sign`) or the client closed it and may start another one.
 */
const RETRY_ELIGIBLE_STATUSES = new Set([
  'in sign',
  ...CLOSED_SIGNATURE_STATUSES,
]);

/**
 * Statuses that must never open another Logalty/DocuSign ceremony.
 * `in summary` and `complete` are not here: the confirmation step still
 * hands off to the first signature, and the BR sign button can run while
 * the application is `in summary`. A `retry=1` link does not use this set
 * to choose the route — see `isSignatureRetryEligible`.
 */
const CEREMONY_BLOCKED_STATUSES = new Set([
  'signed',
  'pending evaluation',
  'quality control',
  'paid',
]);

const USER_CANCEL_ERRORS = new Set([
  'cancel',
  'cancelled',
  'canceled',
  'reject',
  'rejected',
  'rejected by client',
]);

function normalized(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * No status, an open attempt (`in sign`), or the status attachment leaves
 * behind (`upload documents`). `in summary` and `complete` are not included.
 */
function isFirstSignatureStatus(status: string | null | undefined): boolean {
  if (!status) return true;
  const value = normalized(status);
  return value === 'in sign' || value === 'upload documents';
}

export function isClosedSignatureStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return CLOSED_SIGNATURE_STATUSES.has(normalized(status));
}

export function isSignatureRetryEligible(status: string | null | undefined): boolean {
  if (!status) return false;
  return RETRY_ELIGIBLE_STATUSES.has(normalized(status));
}

export function isSignatureCeremonyBlockedStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return CEREMONY_BLOCKED_STATUSES.has(normalized(status));
}

/**
 * Any `error` query on the Logalty return holds the ceremony.
 *
 * MX redirects with a numeric result code (`error=99932`), not the word
 * "cancel". If that redirect lands before the webhook updates the application
 * status, matching only cancel/reject text would reopen the signature.
 * A blank value is treated as absent.
 */
export function hasSignatureError(error: string | null | undefined): boolean {
  return Boolean(error && error.trim());
}

/** User walked away from the ceremony, as opposed to a Logalty/technical code. */
export function isUserCancelledSignatureError(error: string | null | undefined): boolean {
  if (!hasSignatureError(error)) return false;
  return USER_CANCEL_ERRORS.has(normalized(error));
}

export type SignatureHoldVariant = 'cancelled' | 'technical';

/**
 * Copy for the hold screen. A numeric Logalty code (`99932`, `8`) is a
 * technical failure. `cancel` / a closed signature status is the client.
 */
export function signatureHoldVariant(input: {
  signatureError?: string | null;
  applicationStatus?: string | null;
}): SignatureHoldVariant {
  if (hasSignatureError(input.signatureError) && !isUserCancelledSignatureError(input.signatureError)) {
    return 'technical';
  }
  return 'cancelled';
}

/** Query value stored on the hold URL. Technical codes are preserved. */
export function holdErrorQueryValue(signatureError?: string | null): string {
  if (signatureHoldVariant({ signatureError }) === 'technical' && signatureError?.trim()) {
    return signatureError.trim();
  }
  return 'cancel';
}

export type SignatureGate =
  | { action: 'start'; fresh: boolean }
  | { action: 'hold'; variant: SignatureHoldVariant }
  | { action: 'redirect' };

/**
 * What the signature page should do after it has read the application status.
 *
 * `retry=1` starts a new ceremony only for a still-open or client-closed
 * attempt. Any other status leaves the page for the normal resume route and
 * must not call Logalty. An unknown status with `retry=1` holds instead of
 * billing a ceremony we cannot justify.
 */
export function resolveSignatureGate(input: {
  applicationStatus?: string | null;
  signatureError?: string | null;
  explicitRetry?: boolean;
}): SignatureGate {
  if (input.explicitRetry && isSignatureRetryEligible(input.applicationStatus)) {
    return { action: 'start', fresh: true };
  }

  if (isSignatureCeremonyBlockedStatus(input.applicationStatus)) {
    return { action: 'redirect' };
  }

  if (isClosedSignatureStatus(input.applicationStatus)) {
    return {
      action: 'hold',
      variant: signatureHoldVariant(input),
    };
  }

  if (hasSignatureError(input.signatureError)) {
    if (isFirstSignatureStatus(input.applicationStatus)) {
      return { action: 'hold', variant: signatureHoldVariant(input) };
    }
  }

  if (input.explicitRetry) {
    if (!input.applicationStatus) {
      return { action: 'hold', variant: 'technical' };
    }
    return { action: 'redirect' };
  }

  // `in sign` is written only after this page starts the ceremony
  // (signaturePage). Attachment leaves `upload documents`, so that status
  // has to start too — otherwise the resume map sends the client back to
  // /attachment and the two pages bounce. `in summary` and `complete` stay
  // redirects: resume already sends them to confirmation, and opening
  // Logalty from there would skip the document step.
  if (isFirstSignatureStatus(input.applicationStatus)) {
    return { action: 'start', fresh: false };
  }

  return { action: 'redirect' };
}

export function shouldAutoStartDigitalSignature(input: {
  applicationStatus?: string | null;
  signatureError?: string | null;
  explicitRetry?: boolean;
}): boolean {
  if (input.explicitRetry) {
    return isSignatureRetryEligible(input.applicationStatus);
  }
  if (isClosedSignatureStatus(input.applicationStatus)) return false;
  if (hasSignatureError(input.signatureError)) return false;
  return true;
}

/**
 * Drop `retry=1` from the current URL without adding a history entry.
 *
 * Safe when `window.location` is a test stub: failures are ignored.
 * React Router may not observe a raw `replaceState`; the signature page
 * also navigates with `replace` so its own search params update.
 */
export function stripRetryParamFromLocation(): void {
  try {
    const href = window.location?.href;
    if (!href || typeof window.history?.replaceState !== 'function') return;
    const url = new URL(href);
    if (url.searchParams.get('retry') !== '1') return;
    url.searchParams.delete('retry');
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', next);
  } catch {
    // Resume tests replace window.location with a plain object.
  }
}
